import { useForm, yupResolver, UseFormReturnType } from "@mantine/form";
import {
  CreateOrganizationServiceInput,
  useCreateLocationServiceMutation,
  useCreateOrganizationServiceMutation,
  useGetLocationServiceDetailQuery,
  useUpdateLocationServiceMutation,
  useUpdateOrganizationServiceMutation,
} from "src/generated/graphql";
import { useNavigate, useParams } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import { number, object, string } from "yup";
import { memo, useEffect, useState } from "react";
import LocationServiceForm from "./LocationServiceForm";
import { Button, Group, useMantineTheme } from "@mantine/core";
import { useModals } from "@mantine/modals";
import { EMPLOYEE_ROLE_OBJ, MESSAGES, ROUTES } from "src/utils/constants";
import FormCard from "src/components/FormCard";
import { GetLocationServiceDetail } from "src/api/graphql/queries/Service";
import { useAppContext } from "src/api/AppContext";
import useWritePermission from "src/hooks/usePermission";

const locServiceInitialValues = {
  description: "",
  duration: Number(),
  cost: Number(),
  isSelfEnroll: false,
  isPrepay: false,
  isActive: true,
  name: "",
  waitlistMax: Number(),
  revenueCategory: "",
  serviceType: "",
  imgUrl: "",
  policies: [],
  rooms: [],
};

const locServiceFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Service Name should have at least 2 letters")
    .max(30, "Service Name must not be greater than 30 letters")
    .required("Service Name is required"),
  duration: string().required("Duration is required"),
  revenueCategory: string().required("Revenue Group is required"),
  serviceType: string().required("Service type is required"),
  description: string().max(200).notRequired(),
  cost: number().required("Cost is required"),
});

interface IProp {
  isEdit?: boolean;
}
type IFormProps = Omit<
  CreateOrganizationServiceInput,
  "revenueCategory" | "policies" | "serviceType"
> & {
  revenueCategory: string;
  policies: string[];
  serviceType: string;
  rooms: string[];
  cost: number;
  isPrepay: boolean;
  isActive: boolean;
  id?: string;
};

export interface ICreateLocServiceFormProps
  extends UseFormReturnType<IFormProps, (values: IFormProps) => IFormProps> {}

const LocationServiceDetail = ({ isEdit = true }: IProp) => {
  const { currentLocation } = useAppContext();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const modals = useModals();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.locServices);
  const { serviceId } = useParams<{ serviceId: string }>();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(isEdit);
  const refetchQueries = [
    { query: GetLocationServiceDetail, variables: { id: serviceId } },
  ];

  const { data } = useGetLocationServiceDetailQuery({
    variables: { id: serviceId! },
    skip: !serviceId,
    onCompleted: () => {
      resetFormState();
    },
  });

  const [createOrganizationService, { loading: createOrgLoading }] =
    useCreateOrganizationServiceMutation();

  const [createLocationService, { loading: createLocLoading }] =
    useCreateLocationServiceMutation({
      onCompleted: (resp) => {
        if (resp.createLocationService) {
          modals.closeAll();
          navigate(
            `${ROUTES.LOCATION_SERVICE}/${resp.createLocationService.id}`
          );
        } else {
          modals.closeAll();
          window.location.reload();
        }
      },
    });

  const [updateOrganizationService, { loading: updateOrgLoading }] =
    useUpdateOrganizationServiceMutation();

  const [updateLocationService, { loading: updateLocLoading }] =
    useUpdateLocationServiceMutation({
      onCompleted: () => {
        toggleFormActive();
        form.resetDirty();
      },
      refetchQueries,
    });

  const form = useForm<IFormProps>({
    initialValues: locServiceInitialValues,
    validate: yupResolver(locServiceFormSchema),
  });

  const handleSubmit = async (values: IFormProps) => {
    const { cost, rooms, isActive, isPrepay, id, ...rest } = values;
    const orgServiceObj = {
      ...rest,
      revenueCategory: {
        id: values.revenueCategory,
      },
      serviceType: {
        id: values.serviceType,
      },
      policies: values.policies.map((e) => ({ id: e })),
    };

    const locServiceObj = {
      cost,
      isActive,
      isPrepay,
      rooms: values.rooms.map((e) => ({ id: e })),
    };
    if (isEdit) {
      if (!serviceId || !id) return;
      const isUpdateOrg = await updateOrganizationService({
        variables: {
          data: orgServiceObj,
          id,
        },
      });
      if (isUpdateOrg.data?.updateOrganizationService.id) {
        updateLocationService({
          variables: {
            data: {
              ...locServiceObj,
            },
            id: serviceId,
          },
        });
      }
    } else {
      const isOrg = await createOrganizationService({
        variables: {
          data: orgServiceObj,
        },
      });
      if (isOrg.data?.createOrganizationService.id) {
        createLocationService({
          variables: {
            data: {
              ...locServiceObj,
              organizationService: {
                id: isOrg.data?.createOrganizationService.id,
              },
              location: {
                id: currentLocation?.id!,
              },
            },
          },
        });
      }
    }
  };

  const resetFormState = () => {
    if (!data?.locationServiceOne) return;
    const {
      description,
      organizationService,
      imgUrl,
      cost,
      isActive,
      isPrepay,
      rooms,
    } = data?.locationServiceOne;
    const { serviceType, revenueCategory, policies, ...restOrgService } =
      organizationService;
    let _data = {
      ...restOrgService,
      description,
      imgUrl,
      serviceType: serviceType!.id,
      revenueCategory: revenueCategory!.id,
      policies: policies?.map((e) => e.id),
      isActive,
      isPrepay,
      rooms: rooms?.map((e) => e.id),
      cost,
    };
    form.setValues(_data);
    form.resetDirty(_data);
  };

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleCancel = () => {
    resetFormState();
    toggleFormActive();
  };

  const isFormUpdated = form.isDirty() && form.isTouched();
  return (
    <>
      <FormCard
        title={"Basic Info"}
        loading={
          updateOrgLoading ||
          createOrgLoading ||
          createLocLoading ||
          updateLocLoading
        }
        bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
        onCancel={handleCancel}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) => handleSubmit(values))}
        isFormActive={isFormActive}
        hideCard={!isEdit}
      >
        {!isEdit ? (
          <>
            <LocationServiceForm form={form} isFormActive={isFormActive} />
            <Group position="apart" mt="md" style={{ marginTop: 30 }}>
              <Button
                radius="md"
                variant="outline"
                onClick={() => modals.closeAll()}
                style={{
                  flex: 1,
                  color: theme.colors.secondaryTextColor[6],
                }}
                sx={{
                  backgroundColor: theme.colors.secondary[6],
                  "&:hover": {
                    background: theme.colors.secondary[5],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                radius="md"
                type="submit"
                disabled={createOrgLoading || createLocLoading}
                style={{
                  flex: 1,
                }}
                sx={{
                  color: theme.colors.primaryTextColor[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                }}
                loading={createOrgLoading || createLocLoading}
              >
                Create
              </Button>
            </Group>
          </>
        ) : (
          <LocationServiceForm
            form={form}
            isEdit={isEdit}
            isFormActive={isFormActive}
          />
        )}
      </FormCard>
    </>
  );
};

export default memo(LocationServiceDetail);
